import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import { format, isValid, parseISO } from "date-fns";
import { tokens } from "../../public/tokens";
import {
    StyledDefaultInput,
    StyledDivider,
    StyledEnd,
    StyledInfoIcon,
    StyledInputGroup,
    StyledLabel,
    StyledLeadingIcon,
    StyledMessage,
    StyledRequired,
    StyledSelectValue,
    StyledTop,
    StyledValueText,
} from "./SelectDropdown.styles";

const DISPLAY_FORMATS = {
    date: "dd MMM yyyy",
    "datetime-local": "dd MMM yyyy, HH:mm",
};

const DEFAULT_PLACEHOLDERS = {
    date: "Select Date & Time",
    "datetime-local": "Select Date & Time",
};

const StyledDateValue = styled(StyledSelectValue)(({ $disabled = false, $error = false }) => ({
    position: "relative",

    "&:focus-within": ($disabled || $error) ? {} : {
        borderColor: tokens.colors.ui.ring,
    },
}));

// Native input is kept invisible over the whole field so it still owns typing, focus and the picker.
const StyledNativeInput = styled.input({
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: "100%",
    height: "100%",
    padding: 0,
    border: "none",
    opacity: 0,
    cursor: "inherit",

    "&::-webkit-calendar-picker-indicator": {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        width: "100%",
        height: "100%",
        cursor: "inherit",
    },
});

const StyledCalendarIcon = styled(StyledLeadingIcon)({
    padding: `0 ${tokens.globals["spacing-0-5"]}`,
});

/**
 * Formats a native date/datetime-local value for display, or returns "" when it can't be parsed.
 * @param {string} value Native input value (e.g. "2025-09-15" or "2025-09-15T07:15").
 * @param {string} type "date" or "datetime-local".
 * @returns {string} The display text.
 */
function formatDateValue(value, type) {
    if (!value) {
        return "";
    }

    const date = parseISO(value);

    return isValid(date) ? format(date, DISPLAY_FORMATS[type] || DISPLAY_FORMATS.date) : "";
}

export const FieldWrapper = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
});

export const Label = styled.label({
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "20px",
    color: tokens.colors.ui.foreground,
    display: "flex",
    alignItems: "center",
    gap: "4px",

    '&[data-required="true"]::after': {
        content: '"*"',
        color: tokens.colors.ui.destructive,
        marginLeft: "2px",
    },
});

export const ErrorText = styled.span({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "16px",
    color: tokens.colors.ui.destructive,
    minHeight: "1.4em",
});

export const HelperText = styled.span({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "16px",
    color: tokens.colors.ui["muted-foreground"],
});

/**
 * Standard AIE date (or date & time) input, sharing SelectDropdown's field shell and states.
 * @param {string} [props.value] Controlled native value ("yyyy-MM-dd", or "yyyy-MM-ddTHH:mm" for datetime-local).
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange] Change handler.
 * @param {"date"|"datetime-local"} [props.type] Whether the field also picks a time.
 * @param {React.Ref} ref Forwarded to the native input element.
 * Also: defaultValue, placeholder, size, disabled, disabledReason, error, label, required, info, helperText, errorText, id, name, className (self-explanatory).
 */
const DateInput = forwardRef(
    function DateInput(
        {
            value,
            defaultValue = "",
            onChange,
            onKeyDown,
            onClick,
            type = "date",
            placeholder,
            size = "lg",
            disabled = false,
            disabledReason,
            error = false,
            label,
            required = false,
            info,
            helperText,
            errorText,
            id,
            name,
            className,
            ...props
        },
        ref
    ) {
        const inputRef = useRef(null);
        const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);

        useImperativeHandle(ref, () => inputRef.current);

        const inputId = id || name || undefined;
        const messageId = inputId ? `${inputId}-message` : undefined;
        const showError = Boolean(error || errorText);
        const message = errorText || helperText || (disabled ? disabledReason : undefined);
        const displayValue = formatDateValue(value !== undefined ? value : uncontrolledValue, type);

        function openPicker() {
            if (!disabled && typeof inputRef.current?.showPicker === "function") {
                inputRef.current.showPicker();
            }
        }

        function handleChange(event) {
            setUncontrolledValue(event.target.value);

            if (onChange) {
                onChange(event);
            }
        }

        function handleClick(event) {
            openPicker();

            if (onClick) {
                onClick(event);
            }
        }

        function handleKeyDown(event) {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openPicker();
            }

            if (onKeyDown) {
                onKeyDown(event);
            }
        }

        return (
            <StyledInputGroup className={className}>
                <StyledDefaultInput>
                    {label && (
                        <StyledTop>
                            <StyledLabel htmlFor={inputId}>
                                {label}
                                {required && <StyledRequired aria-hidden="true">*</StyledRequired>}
                            </StyledLabel>

                            {info && <StyledInfoIcon className="fal fa-info-circle" title={info} aria-label={info} />}
                        </StyledTop>
                    )}

                    <StyledDateValue
                        $size={size}
                        $disabled={disabled}
                        $error={showError}
                        title={disabled ? disabledReason : undefined}
                    >
                        <StyledValueText $placeholder={!displayValue} aria-hidden="true">
                            {displayValue || placeholder || DEFAULT_PLACEHOLDERS[type] || DEFAULT_PLACEHOLDERS.date}
                        </StyledValueText>

                        <StyledEnd>
                            <StyledDivider aria-hidden="true" />
                            <StyledCalendarIcon className="far fa-calendar-alt" aria-hidden="true" />
                        </StyledEnd>

                        <StyledNativeInput
                            {...props}
                            ref={inputRef}
                            id={inputId}
                            name={name}
                            type={type}
                            value={value}
                            defaultValue={value === undefined ? defaultValue : undefined}
                            onChange={handleChange}
                            onClick={handleClick}
                            onKeyDown={handleKeyDown}
                            disabled={disabled}
                            required={required}
                            aria-invalid={showError || undefined}
                            aria-required={required || undefined}
                            aria-describedby={message ? messageId : undefined}
                        />
                    </StyledDateValue>

                    {message && (
                        <StyledMessage id={messageId} $error={Boolean(errorText)} role={errorText ? "alert" : undefined}>
                            {message}
                        </StyledMessage>
                    )}
                </StyledDefaultInput>
            </StyledInputGroup>
        );
    }
);

DateInput.displayName = "DateInput";

export default DateInput;